import { supabase } from '@/lib/supabase';

export interface FamilyDuplicate {
  id: string;
  name: string;
  created_at: string;
}

export interface ParentDuplicate {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  family_id: string;
  family_name: string;
}

export interface ValidationResult {
  isValid: boolean;
  duplicates: FamilyDuplicate[] | ParentDuplicate[];
  suggestions?: string[];
}

/**
 * Check if a family name already exists in the organization
 */
export const checkFamilyNameExists = async (
  familyName: string, 
  organizationId: string
): Promise<ValidationResult> => {
  try {
    if (!familyName.trim()) {
      return { isValid: true, duplicates: [] };
    }

    const { data: exactMatches, error: exactError } = await supabase
      .from('families')
      .select('id, name, created_at')
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .ilike('name', familyName);

    if (exactError) {
      console.error('Error checking family name:', exactError);
      return { isValid: true, duplicates: [] }; // Allow to proceed if check fails
    }

    // Also check for similar names (fuzzy matching)
    const { data: similarMatches, error: similarError } = await supabase
      .from('families')
      .select('id, name, created_at')
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .ilike('name', `%${familyName.replace(/family|the/gi, '').trim()}%`);

    if (similarError) {
      console.error('Error checking similar family names:', similarError);
    }

    const allMatches = exactMatches || [];
    const similarOnly = (similarMatches || []).filter(
      similar => !allMatches.some(exact => exact.id === similar.id)
    );

    const suggestions = similarOnly.slice(0, 3).map(family => family.name);

    return {
      isValid: allMatches.length === 0,
      duplicates: allMatches as FamilyDuplicate[],
      suggestions: suggestions.length > 0 ? suggestions : undefined
    };

  } catch (error) {
    console.error('Error checking family name:', error);
    return { isValid: true, duplicates: [] }; // Allow to proceed if check fails
  }
};

/**
 * Check if a parent email already exists in the organization
 */
export const checkParentEmailExists = async (
  email: string, 
  organizationId: string
): Promise<ValidationResult> => {
  try {
    if (!email.trim()) {
      return { isValid: true, duplicates: [] };
    }

    const { data: parents, error } = await supabase
      .from('parents')
      .select(`
        id,
        first_name,
        last_name,
        email,
        family_id,
        families!inner(name)
      `)
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .ilike('email', email);

    if (error) {
      console.error('Error checking parent email:', error);
      return { isValid: true, duplicates: [] }; // Allow to proceed if check fails
    }

    const duplicates = (parents || []).map(parent => ({
      id: parent.id,
      first_name: parent.first_name,
      last_name: parent.last_name,
      email: parent.email,
      family_id: parent.family_id,
      family_name: (parent.families as any)?.name || 'Unknown Family'
    }));

    return {
      isValid: duplicates.length === 0,
      duplicates: duplicates as ParentDuplicate[]
    };

  } catch (error) {
    console.error('Error checking parent email:', error);
    return { isValid: true, duplicates: [] }; // Allow to proceed if check fails
  }
};

/**
 * Check if a parent with the same first name, last name, and email exists
 */
export const checkParentExists = async (
  firstName: string,
  lastName: string, 
  email: string,
  organizationId: string
): Promise<ValidationResult> => {
  try {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      return { isValid: true, duplicates: [] };
    }

    const { data: parents, error } = await supabase
      .from('parents')
      .select(`
        id,
        first_name,
        last_name,
        email,
        family_id,
        families!inner(name)
      `)
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .ilike('first_name', firstName)
      .ilike('last_name', lastName)
      .ilike('email', email);

    if (error) {
      console.error('Error checking parent exists:', error);
      return { isValid: true, duplicates: [] }; // Allow to proceed if check fails
    }

    const duplicates = (parents || []).map(parent => ({
      id: parent.id,
      first_name: parent.first_name,
      last_name: parent.last_name,
      email: parent.email,
      family_id: parent.family_id,
      family_name: (parent.families as any)?.name || 'Unknown Family'
    }));

    return {
      isValid: duplicates.length === 0,
      duplicates: duplicates as ParentDuplicate[]
    };

  } catch (error) {
    console.error('Error checking parent exists:', error);
    return { isValid: true, duplicates: [] }; // Allow to proceed if check fails
  }
};

/**
 * Debounced wrapper for validation functions
 */
export const createDebouncedValidator = <T extends any[]>(
  validatorFn: (...args: T) => Promise<ValidationResult>,
  delay: number = 500
) => {
  let timeoutId: NodeJS.Timeout;
  
  return (...args: T): Promise<ValidationResult> => {
    return new Promise((resolve) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        const result = await validatorFn(...args);
        resolve(result);
      }, delay);
    });
  };
};

// Pre-configured debounced validators
export const debouncedFamilyNameCheck = createDebouncedValidator(checkFamilyNameExists);
export const debouncedParentEmailCheck = createDebouncedValidator(checkParentEmailExists);
export const debouncedParentExistsCheck = createDebouncedValidator(checkParentExists);